import { NgModule } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppRoutingModule } from '../app-routing.module';

import { BrowserModule } from '@angular/platform-browser';
import { MainPageComponent } from '@features/main-page/main-page.component';
import { ModelViewerComponent } from '@features/model-viewer/model-viewer.component';
import { ToolBarComponent } from '@features/tool-bar/tool-bar.component';
import { ColorChangeComponent } from '@features/color-change/color-change.component';
import { SizeChangeComponent } from '@features/size-change/size-change.component';

import { MatLegacySliderModule as MatSliderModule } from '@angular/material/legacy-slider';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { ColorPickerComponent } from '@shared/color-picker/color-picker.component';
import { AddBlindComponent } from '@features/add-blind/add-blind.component';
import { BlindListComponent } from '@features/blind-list/blind-list.component';
import { ScrollbarModule } from '@core/plugins/scrollbar/scrollbar.module';
import { HttpClientModule } from '@angular/common/http';
import { SlickCarouselModule } from 'ngx-slick-carousel';
import { TranslateModule } from '@ngx-translate/core';

import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { OperationChangeComponent } from '@features/operation-change/operation-change.component';
import { PipesModule } from '@core/pipes/pipes.module';
import { MountingChangeComponent } from '@features/mounting-change/mounting-change.component';
import { OverviewPageComponent } from '@features/overview-page/overview-page.component';
import { SummaryBlindGalleryComponent } from '@features/summary-blind-gallery/summary-blind-gallery.component';
import { SummaryTableComponent } from '@features/summary-table/summary-table.component';
import { RetailerActionComponent } from '@features/retailer-action/retailer-action.component';
import { RetailerCardComponent } from '@features/retailer-card/retailer-card.component';
import { BlindTypeModalComponent } from '@shared/modals/blind-type-modal/blind-type-modal.component';
import { BlindItemDescriptionComponent } from '@features/blind-list/blind-item-description/blind-item-description.component';
import { ShutterControlComponent } from '@features/shutter-control/shutter-control.component';
import { InputEmailValidator } from '@core/directives/validate/validate-email.directive';
import { OutdoorListComponent } from './blind-list/outdoor-list/outdoor-list.component';
import { InteriorListComponent } from './blind-list/interior-list/interior-list.component';
import { FixturesComponent } from './fixtures/fixtures.component';
import { SharedModule } from '@shared/shared.module';
import { ViewSwitcherComponent } from './view-switcher/view-switcher.component';
import { ControlSwitcherComponent } from './control-switcher/control-switcher.component';
import { ControlPanelComponent } from './control-panel/control-panel.component';
import { ControlModelComponent } from './control-model/control-model.component';

import { DirectivesModule } from '@core/directives/directives.module';
import { SizeDisclaimerComponent } from '@features/size-disclaimer/size-disclaimer.component';
import { IvThemeSwitcherComponent } from '@features/iv-theme-switcher/iv-theme-switcher.component';
import { SelectOpeningScreenComponent } from '@features/select-opening-screen/select-opening-screen.component';
import { OpeningOverlayComponent } from '@features/opening-overlay/opening-overlay.component';
import { ConfirmPopoverComponent } from '@shared/modals/confirm-popover/confirm-popover.component';
import { GalleryFiltersComponent } from '@features/summary-blind-gallery/components/gallery-filters/gallery-filters.component';
import { GallerySidebarComponent } from '@features/summary-blind-gallery/components/gallery-sidebar/gallery-sidebar.component';
import { GalleryCarouselComponent } from '@features/summary-blind-gallery/components/gallery-carousel/gallery-carousel.component';
import { BlindInformationComponent } from '@features/summary-blind-gallery/components/blind-information/blind-information.component';
import { BlindImagesComponent } from '@features/summary-blind-gallery/components/blind-images/blind-images.component';
import { BlindImageComponent } from '@features/summary-blind-gallery/components/blind-image/blind-image.component';
import { BlindActionsComponent } from '@features/summary-blind-gallery/components/blind-actions/blind-actions.component';
import { FrameColorComponent } from '@features/frame-color/frame-color.component';
import { TopStyleComponent } from '@features/top-style/top-style.component';
import { BottomBarComponent } from '@features/bottom-bar/bottom-bar.component';
import { ContactFormComponent } from '@features/contact-form/contact-form.component';
import { QuotationFormComponent } from '@features/quotation-form/quotation-form.component';
import { QuotationPageComponent } from '@features/quotation-page/quotation-page.component';
import { CoreModule } from '@core/core.module';
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { AccordionSectionComponent } from '@shared/accordion-section/accordion-section.component';
import { RetailerCardCompactComponent } from '@features/retailer-card-compact/retailer-card-compact.component';
import { MeasureButtonComponent } from '@features/size-change/components/measure-button/measure-button.component';
import { FormFieldComponent } from '@shared/form-field/form-field.component';
import { PlacesAutocompleteComponent } from '@shared/form-fields/places-autocomplete/places-autocomplete.component';
import { SelectComponent } from '@shared/form-fields/select/select.component';

@NgModule({
	declarations: [
		MainPageComponent,
		ModelViewerComponent,
		ToolBarComponent,
		ColorChangeComponent,
		SizeChangeComponent,
		AddBlindComponent,
		BlindListComponent,
		OperationChangeComponent,
		MountingChangeComponent,
		OverviewPageComponent,
		SummaryBlindGalleryComponent,
		GalleryFiltersComponent,
		GallerySidebarComponent,
		GalleryCarouselComponent,
		BlindInformationComponent,
		BlindActionsComponent,
		BlindImagesComponent,
		BlindImageComponent,
		SummaryTableComponent,
		RetailerActionComponent,
		RetailerCardComponent,
		ColorPickerComponent,
		BlindTypeModalComponent,
		BlindItemDescriptionComponent,
		ShutterControlComponent,
		InputEmailValidator,
		OutdoorListComponent,
		InteriorListComponent,
		FixturesComponent,
		ViewSwitcherComponent,
		IvThemeSwitcherComponent,
		ControlSwitcherComponent,
		ControlPanelComponent,
		ControlModelComponent,
		SizeDisclaimerComponent,
		SelectOpeningScreenComponent,
		OpeningOverlayComponent,
		FrameColorComponent,
		TopStyleComponent,
		BottomBarComponent,
		QuotationPageComponent,
		ContactFormComponent,
		QuotationFormComponent,
	],
	exports: [BlindTypeModalComponent],
	imports: [
		CommonModule,
		BrowserModule,
		MatSliderModule,
		MatDialogModule,
		MatExpansionModule,
		HttpClientModule,
		ScrollbarModule,
		FormsModule,
		ReactiveFormsModule,
		NgbModule,
		PipesModule,
		AppRoutingModule,
		SlickCarouselModule,
		TranslateModule,
		SharedModule,
		DirectivesModule,
		ConfirmPopoverComponent,
		NgOptimizedImage,
		CoreModule,
		CdkAccordionModule,
		AccordionSectionComponent,
		RetailerCardCompactComponent,
		MeasureButtonComponent,
		FormFieldComponent,
		PlacesAutocompleteComponent,
		SelectComponent,
	],
})
export class FeaturesModule {}
